import React, { useState, useEffect, useMemo } from "react";
import { GetSammelanEventProfiles, GetSammelanEvents } from "./apis/get";
import {
  PostAssignUserSammelan,
  PostCreateSmmelanEvent,
  PostSammelanEventStatus,
  PostDeleteSammelanEvent,
} from "./apis/post";
import {
  Calendar,
  Search,
  User,
  Mail,
  Phone,
  Clock,
  RefreshCw,
  PlusCircle,
  Trash2,
} from "lucide-react";
import LoaderService from "../services/LoaderService";
import TableCardView from "./ui/tableCardView";
import ConfirmationModal from "./ConfirmationModal";
import { toast } from "react-hot-toast";

const EventsManagement = () => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [newEventName, setNewEventName] = useState("");
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const fetchProfiles = async (search = "") => {
    setLoading(true);
    LoaderService.show();
    try {
      const response = await GetSammelanEventProfiles(1000, 0, search);
      if (
        response.status === "success" ||
        response.success === true ||
        response.status === true
      ) {
        setProfiles(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch sammelan event profiles", error);
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await GetSammelanEvents();
      if (response.status === true) {
        setEvents(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch sammelan events", error);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    setIsCreatingEvent(true);
    LoaderService.show();
    try {
      const response = await PostCreateSmmelanEvent({ name: newEventName });
      if (response.status === "success" || response.status === true) {
        toast.success("Event created successfully");
        setNewEventName("");
        fetchProfiles();
        fetchEvents();
      } else {
        toast.error(response.message || "Failed to create event");
      }
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Error creating event");
    } finally {
      setIsCreatingEvent(false);
      LoaderService.hide();
    }
  };
  const handleToggleEventStatus = async (eventId: number, status: boolean) => {
    LoaderService.show();
    try {
      const response = await PostSammelanEventStatus({
        sammelan_event_id: eventId,
        is_active: status,
      });
      if (response.status === true) {
        toast.success(
          `Event ${status ? "activated" : "deactivated"} successfully`,
        );
        fetchEvents();
      } else {
        toast.error(response.message || "Failed to update event status");
      }
    } catch (error) {
      console.error("Error toggling event status:", error);
      toast.error("Error toggling event status");
    } finally {
      LoaderService.hide();
    }
  };

  const handleDeleteEvent = (eventId: number, eventName: string) => {
    setDeleteTarget({ id: eventId, name: eventName });
    setShowDeleteModal(true);
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget) return;
    setShowDeleteModal(false);

    LoaderService.show();
    try {
      const response = await PostDeleteSammelanEvent({
        sammelan_event_id: deleteTarget.id,
      });
      if (response.status === true) {
        toast.success("Sammelan event deleted successfully");
        fetchEvents();
      } else {
        toast.error(
          response.detail || response.message || "Failed to delete event",
        );
      }
    } catch (error) {
      console.error("Error deleting sammelan event:", error);
      toast.error("Error deleting event");
    } finally {
      LoaderService.hide();
      setDeleteTarget(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProfiles(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const columns = [
    {
      label: "User",
      accessor: "username",
      cellRenderer: (row: any) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
            <User size={18} className="text-indigo-400" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-slate-200">
              {row.username || "N/A"}
            </span>
            <span className="text-[10px] font-mono text-slate-500">
              {row.user_id}
            </span>
          </div>
        </div>
      ),
    },
    {
      label: "Event",
      accessor: "event_name",
      cellRenderer: (row: any) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-lg w-fit">
            <Calendar size={12} className="text-indigo-400" />
            <span className="text-[11px] font-black text-indigo-400 uppercase tracking-tight">
              {row.event_name || "N/A"}
            </span>
          </div>
          <span className="text-[9px] text-slate-500 font-mono font-bold tracking-tighter ml-1">
            ID: {row.sammelan_event_id}
          </span>
        </div>
      ),
    },
    {
      label: "Event Created",
      accessor: "event_created_at",
      cellRenderer: (row: any) => (
        <div className="flex flex-col text-[10px] text-slate-500 font-medium">
          <div className="flex items-center gap-1">
            <Calendar size={10} className="text-slate-600" />
            <span>
              {row.event_created_at
                ? new Date(row.event_created_at).toLocaleDateString()
                : "N/A"}
            </span>
          </div>
        </div>
      ),
    },
    {
      label: "Assigned At",
      accessor: "assigned_at",
      cellRenderer: (row: any) => (
        <div className="flex flex-col text-[10px] text-slate-500 font-medium">
          <div className="flex items-center gap-1">
            <Clock size={10} className="text-indigo-400/50" />
            <span className="text-slate-300 font-bold">
              {row.assigned_at
                ? new Date(row.assigned_at).toLocaleDateString()
                : "N/A"}
            </span>
          </div>
          <span className="opacity-60 ml-3.5 italic">
            {row.assigned_at
              ? new Date(row.assigned_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </span>
        </div>
      ),
    },
  ];

  const filteredProfiles = profiles;

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <div className="p-6 bg-transparent min-h-screen text-white">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 ring-4 ring-indigo-500/10 scale-110">
              <Calendar size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white uppercase tracking-tighter">
                Sammelan Events
              </h1>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-0.5">
                Event Participant Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative group flex-1 md:w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search
                  size={16}
                  className="text-slate-500 group-focus-within:text-indigo-400 transition-colors"
                />
              </div>
              <input
                type="text"
                placeholder="Search Participants..."
                className="w-full bg-slate-900/50 border border-slate-700/50 text-white text-sm rounded-xl px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all placeholder:text-slate-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => fetchProfiles(searchTerm)}
              className="p-2.5 bg-slate-900/50 border border-slate-700/50 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all group"
            >
              <RefreshCw
                size={20}
                className="group-active:rotate-180 transition-transform duration-500"
              />
            </button>
          </div>
        </div>

        {/* Active Events Section */}
        <div className="flex flex-col gap-4 animate-fade-in">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
            <Calendar size={12} /> Live Events ({events.length})
          </h3>
          <div className="flex flex-wrap gap-3">
            {events.map((event: any) => (
              <div
                key={event.id}
                className="px-4 py-3 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl flex items-center gap-4 group hover:bg-indigo-500/10 transition-all cursor-default min-w-[280px]"
              >
                <div
                  className={`w-2 h-2 rounded-full ${event.is_active ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" : "bg-slate-600"}`}
                />
                <div className="flex-1 flex flex-col">
                  <span
                    className={`text-xs font-black uppercase tracking-tight ${event.is_active ? "text-indigo-300" : "text-slate-500"}`}
                  >
                    {event.name}
                  </span>
                  <span className="text-[9px] text-slate-600 font-mono font-bold uppercase tracking-tighter">
                    ID: {event.id} •{" "}
                    {new Date(event.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      handleToggleEventStatus(event.id, !event.is_active)
                    }
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 border ${
                      event.is_active
                        ? "bg-rose-500/10 text-rose-500 border-rose-500/20 hover:bg-rose-500/20"
                        : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20"
                    }`}
                  >
                    {event.is_active ? "Disable" : "Enable"}
                  </button>

                  <button
                    onClick={() => handleDeleteEvent(event.id, event.name)}
                    className="p-2 rounded-full text-slate-400 bg-slate-900/60 border border-slate-700/50 hover:text-white hover:bg-rose-500/10 transition-all active:scale-95"
                    title="Delete Sammelan event"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <span className="text-[10px] text-slate-600 italic uppercase font-bold px-1">
                No active events found
              </span>
            )}
          </div>
        </div>

        {/* Create Event Section */}
        <div className="bg-gradient-to-r from-slate-900/50 to-indigo-900/10 backdrop-blur-xl border border-white/5 rounded-3xl p-6 shadow-2xl flex flex-col md:flex-row items-center gap-6 animate-fade-in">
          <div className="flex-1 flex flex-col gap-1">
            <h2 className="text-sm font-black text-indigo-400 uppercase tracking-widest flex items-center gap-2">
              <PlusCircle size={14} /> Create New Event
            </h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">
              Initialize a new Sammelan event to start tracking participants
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative group flex-1 md:w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PlusCircle
                  size={14}
                  className="text-slate-500 group-focus-within:text-indigo-400 transition-colors"
                />
              </div>
              <input
                type="text"
                placeholder="Enter Event Name (e.g. Sammelan Fest 2026)"
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-indigo-500/50 font-bold text-white text-xs rounded-xl px-10 py-3 transition-all placeholder:text-slate-700"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                disabled={isCreatingEvent}
              />
            </div>
            <button
              onClick={handleCreateEvent}
              disabled={isCreatingEvent || !newEventName.trim()}
              className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 ${
                isCreatingEvent || !newEventName.trim()
                  ? "bg-slate-800 text-slate-500 border border-slate-700"
                  : "bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/20"
              }`}
            >
              {isCreatingEvent ? "Creating..." : "Launch Event"}
            </button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              label: "Total Participants",
              value: profiles.length,
              color: "text-indigo-400",
              bg: "bg-indigo-500/10 border-indigo-500/20",
            },
            {
              label: "Assigned Today",
              value: profiles.filter(
                (p) =>
                  p.assigned_at &&
                  new Date(p.assigned_at).toDateString() ===
                    new Date().toDateString(),
              ).length,
              color: "text-emerald-400",
              bg: "bg-emerald-500/10 border-emerald-500/20",
            },
            {
              label: "Total Events",
              value: events.length,
              color: "text-blue-400",
              bg: "bg-blue-500/10 border-blue-500/20",
            },
            {
              label: "Latest Event",
              value: events.length > 0 ? events[0].name : "N/A",
              color: "text-pink-400",
              bg: "bg-pink-500/10 border-pink-500/20",
            },
          ].map((card, i) => (
            <div
              key={i}
              className={`${card.bg} border backdrop-blur-md p-6 rounded-2xl transition-all hover:scale-[1.02] shadow-xl group`}
            >
              <div className="flex flex-col">
                <span
                  className={
                    card.label === "Latest Event"
                      ? `text-xs font-black ${card.color} tracking-tighter truncate w-full`
                      : `text-3xl font-black ${card.color} tracking-tighter`
                  }
                >
                  {card.value}
                </span>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1 opacity-70 group-hover:opacity-100 transition-opacity">
                  {card.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Table View */}
        <div className="bg-slate-900/30 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl min-h-[400px]">
          <TableCardView
            data={filteredProfiles}
            columns={columns}
            view="table"
            sortConfig={sortConfig}
            onSort={handleSort}
            classtype="sammelan_event_profiles"
          />
        </div>
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDeleteEvent}
          title="Confirm Delete"
          description={
            deleteTarget
              ? `Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`
              : "Are you sure you want to delete this Sammelan event?"
          }
          variant="danger"
        />
      </div>
    </div>
  );
};

export default EventsManagement;
